import axios from 'axios';
import { auth } from '../config/firebase';

// Check if we're running on localhost (development/local testing)
const isLocal = window.location.hostname === 'localhost' || 
                window.location.hostname === '127.0.0.1' || 
                window.location.hostname.includes('localhost');

const API_URL = isLocal 
  ? process.env.REACT_APP_API_URL 
  : process.env.REACT_APP_API_URL_PROD;

console.log('API Configuration:', {
  hostname: window.location.hostname,
  isLocal,
  selectedURL: API_URL,
  localURL: process.env.REACT_APP_API_URL,
  prodURL: process.env.REACT_APP_API_URL_PROD
});

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  async (config) => {
    const user = auth.currentUser;
    if (user) {
      const token = await user.getIdToken();
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Auth Services
export const authService = {
  updateProfile: (data) => api.post('/auth/profile', data),
  getProfile: () => api.get('/auth/profile'),
  updatePreferences: (preferences) => api.put('/auth/preferences', preferences),
};

// Product Services
export const productService = {
  getProducts: (params) => api.get('/products', { params }), // Alias for consistency
  getAllProducts: (params) => api.get('/products', { params }),
  getProduct: (id) => api.get(`/products/${id}`),
  getProductReviews: (id, params) => api.get(`/products/${id}/reviews`, { params }),
  getProductReviewSummary: (id) => api.get(`/products/${id}/reviews/summary`),
  createProductReview: (id, data) => api.post(`/products/${id}/reviews`, data),
  getMyReviews: (params) => api.get('/products/me/reviews', { params }),
  getSimilarProducts: (id, params) => api.get(`/products/${id}/similar`, { params }),
  getFeaturedProducts: () => api.get('/products/featured'),
  getProductLabels: () => api.get('/products/labels'),
  getProductsByCategory: (category, params) => api.get(`/products/category/${category}`, { params }),
  searchProducts: (query, params) => api.get(`/products/search/${query}`, { params }),
  getCategories: () => api.get('/products/meta/categories'),
  
  // Admin only
  getAllProductsAdmin: (params) => api.get('/products/admin/all', { params }),
  createProduct: (data) => api.post('/products', data),
  updateProduct: (id, data) => api.put(`/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/products/${id}`),
  addToWishlist: (id) => api.post(`/products/${id}/wishlist`),
  removeFromWishlist: (id) => api.delete(`/products/${id}/wishlist`),
  getMyWishlist: () => api.get('/products/me/wishlist'),
};

// Cart Services
export const cartService = {
  getCart: () => api.get('/cart'),
  addToCart: (data) => api.post('/cart/add', data),
  updateCart: (data) => api.put('/cart/update', data),
  removeFromCart: (productId, customizations) => api.delete(`/cart/remove/${productId}`, { 
    data: { customizations } 
  }),
  clearCart: () => api.delete('/cart/clear'),
  validateCoupon: (code, cartData) => api.post('/coupons/validate', { code, cartData }),
  applyCoupon: (code, cartData) => api.post('/coupons/apply', { code, cartData }),
  replaceCart: (items) => api.post('/cart/batch', { items, mode: 'replace' }),
};

// Order Services
export const orderService = {
  createOrder: (data) => api.post('/orders', data),
  createPaymentSession: (data) => api.post('/orders/session', data),
  getOrders: (params) => api.get('/orders', { params }),
  getOrder: (id) => api.get(`/orders/${id}`),
  uploadOrderImages: (id, formData) => api.post(`/orders/${id}/images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateCustomization: (id, data) => api.put(`/orders/${id}/customization`, data),
  cancelOrder: (id) => api.put(`/orders/${id}/cancel`),
  retryPayment: (id) => api.post(`/orders/${id}/retry-payment`),
  updateOrderStatus: (id, status, paymentStatus, reason) => api.put(`/orders/${id}/status`, {
    status,
    paymentStatus,
    reason
  }),
};

// Upload Services
export const uploadService = {
  uploadProductImages: (productId, formData) => api.post(`/upload/products/${productId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadReviewImages: (productId, formData) => api.post(`/upload/reviews/${productId}`, formData, { headers:{ 'Content-Type': 'multipart/form-data' }}),
  uploadOrderImages: (orderId, formData) => api.post(`/upload/orders/${orderId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getOrderImages: (orderId) => api.get(`/upload/orders/${orderId}/images`),
  uploadProfileImage: (formData) => api.post('/upload/profile', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteImage: (fileName) => api.delete('/upload/images', { data: { fileName } }),
};

// Shipping Services
export const shippingService = {
  getSettings: () => api.get('/shipping/settings'),
  getQuote: (payload) => api.post('/shipping/quote', payload),
  updateSettings: (data) => api.put('/shipping/settings', data),
};

// Admin Services
export const adminService = {
  getDashboardStats: () => api.get('/admin/dashboard'),
  getRecentOrders: (limit = 5) => api.get('/admin/orders/recent', { params: { limit } }),
  
  // Orders
  getAllOrders: (params) => api.get('/orders/admin/all', { params }),
  getOrders: (params) => api.get('/admin/orders', { params }),
  updateOrderStatus: (id, status, notes) => api.put(`/orders/admin/${id}/status`, { status, notes }),
  
  // Image Downloads
  downloadOrderImages: (orderId) => api.get(`/orders/admin/${orderId}/download-images`, { 
    responseType: 'blob' 
  }),
  downloadSingleImage: (orderId, itemIndex, imageIndex) => api.get(
    `/orders/admin/${orderId}/download-image/${itemIndex}/${imageIndex}`, 
    { responseType: 'blob' }
  ),
  downloadOrderImages: (orderId) => api.get(`/orders/admin/${orderId}/download-images`, { responseType: 'blob' }),
  getHighResImage: (orderId, itemIndex, imageIndex) => api.get(`/orders/admin/${orderId}/image/${itemIndex}/${imageIndex}/high-res`),
  
  // Users
  getUsers: (params) => api.get('/admin/users', { params }),
  
  // Categories
  getCategories: () => api.get('/admin/categories'),
  getCategoriesTree: () => api.get('/admin/categories/tree'),
  createCategory: (data) => api.post('/admin/categories', data),
  updateCategory: (id, data) => api.put(`/admin/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/admin/categories/${id}`),
  forceDeleteCategory: (id, data) => api.delete(`/admin/categories/${id}/force`, { data }),
  getProductsByCategories: (categoryIds) => api.get('/admin/products/by-categories', { params: { categoryIds } }),
  bulkUpdateProductsCategories: (data) => api.put('/admin/products/bulk-category', data),
  
  // Coupons
  getCoupons: (params) => api.get('/admin/coupons', { params }),
  createCoupon: (data) => api.post('/admin/coupons', data),
  updateCoupon: (id, data) => api.put(`/admin/coupons/${id}`, data),
  deleteCoupon: (id) => api.delete(`/admin/coupons/${id}`),
  toggleCouponStatus: (id, data) => api.put(`/admin/coupons/${id}/status`, data),
};

// Public (non-auth) service helpers
export const publicService = {
  submitContact: (data) => api.post('/admin/contact', data)
};

// Admin contact messages (could also be grouped under adminService but separated for clarity)
adminService.getContactMessages = (status = 'all') => api.get('/admin/contact-messages', { params: { status } });
adminService.updateContactMessage = (id, data) => api.patch(`/admin/contact-messages/${id}`, data);

export default api;
